import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageSquare, Mail, User, Clock, X, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

interface ContactMessage {
  id: string
  name: string
  email: string
  company: string | null
  phone: string | null
  subject: string
  message: string
  is_read: boolean
  created_at: string
}

export function MessagesPage() {
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null)
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')
  const queryClient = useQueryClient()

  const { data: messages, isLoading } = useQuery({
    queryKey: ['admin-messages', filter],
    queryFn: async () => {
      let query = supabase.from('contact_messages').select('*').order('created_at', { ascending: false })
      if (filter === 'unread') query = query.eq('is_read', false)
      if (filter === 'read') query = query.eq('is_read', true)
      const { data, error } = await query
      if (error) throw error
      return data as ContactMessage[]
    },
  })

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('contact_messages').update({ is_read: true }).eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-messages'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('contact_messages').delete().eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-messages'] })
      setSelectedMessage(null)
    },
  })

  const openMessage = (msg: ContactMessage) => {
    setSelectedMessage(msg)
    if (!msg.is_read) markReadMutation.mutate(msg.id)
  }

  const unreadCount = messages?.filter((m) => !m.is_read).length || 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Messages</h1>
        <p className="text-secondary-600">Contact form submissions</p>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {(['all', 'unread', 'read'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize',
              filter === f ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            )}
          >
            {f} {f === 'unread' && unreadCount > 0 && `(${unreadCount})`}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : messages && messages.length > 0 ? (
          <div className="divide-y divide-secondary-100">
            {messages.map((msg) => (
              <div
                key={msg.id}
                onClick={() => openMessage(msg)}
                className={cn(
                  'p-4 cursor-pointer hover:bg-secondary-50 transition-colors',
                  !msg.is_read && 'bg-primary-50/50'
                )}
              >
                <div className="flex items-start gap-4">
                  <div className={cn(
                    'w-2 h-2 rounded-full mt-2 flex-shrink-0',
                    msg.is_read ? 'bg-secondary-300' : 'bg-primary-600'
                  )} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn('font-semibold', !msg.is_read && 'text-secondary-900')}>
                        {msg.name}
                      </span>
                      {msg.company && (
                        <span className="text-sm text-secondary-500">({msg.company})</span>
                      )}
                    </div>
                    <p className="font-medium text-secondary-700 line-clamp-1">{msg.subject}</p>
                    <p className="text-sm text-secondary-500 line-clamp-1">{msg.message}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-secondary-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(msg.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No messages found</p>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <AnimatePresence>
        {selectedMessage && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedMessage(null)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto"
            >
              <div className="p-6 border-b sticky top-0 bg-white flex items-center justify-between">
                <h2 className="text-lg font-semibold">Message Details</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (confirm('Delete this message?')) deleteMutation.mutate(selectedMessage.id)
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <button onClick={() => setSelectedMessage(null)} className="p-2 hover:bg-secondary-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-secondary-500 flex items-center gap-1">
                      <User className="w-4 h-4" /> Name
                    </label>
                    <p className="font-medium text-secondary-900">{selectedMessage.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-secondary-500 flex items-center gap-1">
                      <Mail className="w-4 h-4" /> Email
                    </label>
                    <a href={`mailto:${selectedMessage.email}`} className="font-medium text-primary-600">
                      {selectedMessage.email}
                    </a>
                  </div>
                  {selectedMessage.company && (
                    <div>
                      <label className="text-sm text-secondary-500">Company</label>
                      <p className="font-medium text-secondary-900">{selectedMessage.company}</p>
                    </div>
                  )}
                  {selectedMessage.phone && (
                    <div>
                      <label className="text-sm text-secondary-500">Phone</label>
                      <a href={`tel:${selectedMessage.phone}`} className="font-medium text-primary-600">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm text-secondary-500">Subject</label>
                  <p className="font-semibold text-secondary-900 text-lg">{selectedMessage.subject}</p>
                </div>

                <div>
                  <label className="text-sm text-secondary-500">Message</label>
                  <p className="text-secondary-700 whitespace-pre-wrap mt-2">{selectedMessage.message}</p>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-secondary-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Received: {new Date(selectedMessage.created_at).toLocaleString()}
                  </p>
                </div>

                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="btn-primary w-full"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Reply via Email
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
